import { BlockId } from './BlockType.js';
import { MAX_GROWTH_STAGE, CROP_GROWTH_TICK_INTERVAL, CROP_GROWTH_CHANCE, HYDRATION_RADIUS } from '../constants.js';

/**
 * FarmingSystem - Tracks planted crops and handles growth ticks.
 *
 * Crops grow in stages (0 to MAX_GROWTH_STAGE). Every CROP_GROWTH_TICK_INTERVAL
 * seconds there is a CROP_GROWTH_CHANCE probability each tracked crop advances
 * one stage. When fully grown the crop block remains but is considered mature.
 *
 * Supported crops: WHEAT, CARROT, POTATO, BEETROOT, BERRY_BUSH,
 * PUMPKIN_STEM, MELON_STEM, SUGAR_CANE.
 *
 * Bee pollination: if bees are nearby, growth chance doubles.
 */

// All block IDs that count as crops
const CROP_BLOCKS = new Set([
  BlockId.WHEAT,
  BlockId.CARROT,
  BlockId.POTATO,
  BlockId.BEETROOT,
  BlockId.BERRY_BUSH,
  BlockId.PUMPKIN_STEM,
  BlockId.MELON_STEM,
  BlockId.SUGAR_CANE,
]);

export class FarmingSystem {
  constructor(world) {
    this.world = world;
    this._tickTimer = 0;
    this._trackedCrops = new Map(); // "x,y,z" -> { growthStage: 0..MAX_GROWTH_STAGE, type: blockId }
    this._beePositions = []; // set from EntityManager each frame
  }

  /**
   * Set bee positions for pollination boost (called from main.js each tick).
   */
  setBeePositions(positions) {
    this._beePositions = positions || [];
  }

  /**
   * Check if any bee is within radius of a position.
   */
  _hasBeeNearby(x, y, z, radius = 8) {
    for (const bp of this._beePositions) {
      const dx = bp.x - x;
      const dy = bp.y - y;
      const dz = bp.z - z;
      if (dx * dx + dy * dy + dz * dz < radius * radius) return true;
    }
    return false;
  }

  /**
   * Called every frame from the game loop.
   */
  update(dt) {
    this._tickTimer += dt;
    if (this._tickTimer < CROP_GROWTH_TICK_INTERVAL) return;
    this._tickTimer = 0;

    for (const [key, crop] of this._trackedCrops) {
      const [x, y, z] = key.split(',').map(Number);
      // Check block still exists and is a crop
      const block = this.world.getBlock(x, y, z);
      if (!CROP_BLOCKS.has(block)) {
        this._trackedCrops.delete(key);
        continue;
      }

      // Growth chance (doubled with bee nearby)
      let growthChance = CROP_GROWTH_CHANCE;
      if (this._hasBeeNearby(x, y, z)) {
        growthChance *= 2;
      }

      // Sugar cane: grows upward instead of stages
      if (block === BlockId.SUGAR_CANE) {
        this._growSugarCane(x, y, z, growthChance);
        continue;
      }

      // Pumpkin/Melon stem: at max stage, try to place fruit
      if ((block === BlockId.PUMPKIN_STEM || block === BlockId.MELON_STEM) && crop.growthStage >= MAX_GROWTH_STAGE) {
        this._tryPlaceFruit(x, y, z, block);
        continue;
      }

      // Normal crop growth
      if (crop.growthStage < MAX_GROWTH_STAGE && Math.random() < growthChance) {
        crop.growthStage++;
        if (this.world.markChunkDirtyAt) this.world.markChunkDirtyAt(x, y, z);
      }

      // Update farmland hydration below the crop
      const below = this.world.getBlock(x, y - 1, z);
      if (below === BlockId.FARMLAND) {
        if (FarmingSystem.isHydrated(this.world, x, y - 1, z)) {
          this.world.setBlock(x, y - 1, z, BlockId.FARMLAND_WET);
        }
      }
    }
  }

  /**
   * Sugar cane grows upward (max 3 tall). Needs water adjacent at base.
   */
  _growSugarCane(x, y, z, growthChance) {
    if (Math.random() >= growthChance) return;

    // Find top of sugar cane stack
    let topY = y;
    while (this.world.getBlock(x, topY + 1, z) === BlockId.SUGAR_CANE) topY++;

    // Max height 3
    const height = topY - y + 1;
    if (height >= 3) return;

    // Check above is air
    if (this.world.getBlock(x, topY + 1, z) !== BlockId.AIR) return;

    // Check base has water nearby
    const baseY = y;
    let hasWater = false;
    for (const [dx, dz] of [[1,0],[-1,0],[0,1],[0,-1]]) {
      if (this.world.getBlock(x + dx, baseY, z + dz) === BlockId.WATER ||
          this.world.getBlock(x + dx, baseY - 1, z + dz) === BlockId.WATER) {
        hasWater = true;
        break;
      }
    }
    if (!hasWater) return;

    this.world.setBlock(x, topY + 1, z, BlockId.SUGAR_CANE);
    if (this.world.markChunkDirtyAt) this.world.markChunkDirtyAt(x, topY + 1, z);
  }

  /**
   * Pumpkin/Melon stem: when mature, place fruit on adjacent air block.
   */
  _tryPlaceFruit(x, y, z, stemBlock) {
    if (Math.random() >= 0.1) return; // 10% chance per tick

    const fruitBlock = stemBlock === BlockId.PUMPKIN_STEM ? BlockId.PUMPKIN : BlockId.MELON;
    const offsets = [[1,0],[-1,0],[0,1],[0,-1]];
    const shuffled = offsets.sort(() => Math.random() - 0.5);

    for (const [dx, dz] of shuffled) {
      const fx = x + dx;
      const fz = z + dz;
      if (this.world.getBlock(fx, y, fz) === BlockId.AIR &&
          this.world.getBlock(fx, y - 1, fz) !== BlockId.AIR) {
        this.world.setBlock(fx, y, fz, fruitBlock);
        if (this.world.markChunkDirtyAt) this.world.markChunkDirtyAt(fx, y, fz);
        return;
      }
    }
  }

  /**
   * Register a crop when seeds are planted.
   */
  plantCrop(x, y, z, blockId) {
    this._trackedCrops.set(`${x},${y},${z}`, { growthStage: 0, type: blockId || BlockId.WHEAT });
  }

  /**
   * Remove tracking when a crop is harvested / broken.
   */
  removeCrop(x, y, z) {
    this._trackedCrops.delete(`${x},${y},${z}`);
  }

  /**
   * Check if farmland at (x, y, z) has water within HYDRATION_RADIUS blocks.
   */
  static isHydrated(world, x, y, z) {
    for (let dx = -HYDRATION_RADIUS; dx <= HYDRATION_RADIUS; dx++) {
      for (let dz = -HYDRATION_RADIUS; dz <= HYDRATION_RADIUS; dz++) {
        if (world.getBlock(x + dx, y, z + dz) === BlockId.WATER ||
            world.getBlock(x + dx, y - 1, z + dz) === BlockId.WATER) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Apply bone meal: instantly grow crop to max stage.
   */
  applyBoneMeal(x, y, z) {
    const key = `${x},${y},${z}`;
    const crop = this._trackedCrops.get(key);
    if (crop) {
      crop.growthStage = MAX_GROWTH_STAGE;
      if (this.world.markChunkDirtyAt) this.world.markChunkDirtyAt(x, y, z);
      return true;
    }
    return false;
  }

  /**
   * Get the growth stage of a crop.
   */
  getGrowthStage(x, y, z) {
    const crop = this._trackedCrops.get(`${x},${y},${z}`);
    return crop ? crop.growthStage : MAX_GROWTH_STAGE;
  }

  serialize() {
    const crops = [];
    for (const [key, crop] of this._trackedCrops) {
      crops.push({ key, stage: crop.growthStage, type: crop.type });
    }
    return { crops };
  }

  deserialize(data) {
    if (!data || !data.crops) return;
    this._trackedCrops.clear();
    for (const c of data.crops) {
      this._trackedCrops.set(c.key, { growthStage: c.stage, type: c.type || BlockId.WHEAT });
    }
  }
}
